import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  LeftCircleOutlined,
  PrinterOutlined,
  SignatureOutlined,
} from '@ant-design/icons';
import { Tag } from 'antd';
import { AButton, ATitle, Loading, ToSignature } from '../../../../components';
import {
  IDentalTreatment,
  IDentalTreatmentItem,
} from '../../../../shared/interfaces';
import { useToast } from '../../../../hooks/toast';
import api from '../../../../services/api';
import { ContainerMenuPrint } from './styles';
import { DentalTreatmentProcedureReport } from '../../../../shared/reports/DentalTreatmentProcedure';

interface IDentalTreatmentEvolutionLog {
  id: string;
  dental_treatment_item_id?: string;
  date?: string;
  time?: string;
  created_at?: string;
  procedure_id?: string;
  professional_id?: string;
  signature_validation_code?: string | null;
  signed_at?: string | null;
}

const DentalTreatmentProcedurePrint: React.FC = () => {
  const { id, itemId } = useParams<{ id: string; itemId: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [loading, setLoading] = useState<boolean>(true);
  const [treatment, setTreatment] = useState<IDentalTreatment | null>(null);
  const [item, setItem] = useState<IDentalTreatmentItem | null>(null);
  const [modalSign, setModalSign] = useState<boolean>(false);
  const [validationCode, setValidationCode] = useState<string | null>(null);
  const [signedAt, setSignedAt] = useState<string | null>(null);
  const [latestLogId, setLatestLogId] = useState<string | null>(null);
  const [latestLogProfessionalId, setLatestLogProfessionalId] = useState<
    string | null
  >(null);
  const [signature, setSignature] = useState<string | null>(null);
  const [signerName, setSignerName] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!id || !itemId) return;
    setLoading(true);
    try {
      const [treatmentResponse, logsResponse] = await Promise.all([
        api.get(`/dental-treatments/${id}`),
        api.get(`/dental-treatments-logs/list/${id}`),
      ]);
      const { treatment, items } = treatmentResponse.data;
      const foundItem: IDentalTreatmentItem | undefined = items.find(
        (i: IDentalTreatmentItem) => i.id === itemId,
      );

      const rawLogs = (logsResponse.data ||
        []) as IDentalTreatmentEvolutionLog[];
      const itemLogs = rawLogs.filter(
        (log) =>
          log.dental_treatment_item_id &&
          String(log.dental_treatment_item_id) === String(itemId),
      );

      const procedureLogs = rawLogs.filter(
        (log) =>
          log.procedure_id &&
          foundItem?.procedure_id &&
          String(log.procedure_id) === String(foundItem.procedure_id),
      );

      const candidateLogs = itemLogs.length > 0 ? itemLogs : procedureLogs;

      candidateLogs.sort((a, b) => {
        const first = new Date(
          `${a.date || a.created_at || ''}T${a.time || '00:00:00'}`,
        ).valueOf();
        const second = new Date(
          `${b.date || b.created_at || ''}T${b.time || '00:00:00'}`,
        ).valueOf();
        return second - first;
      });

      const latestLog = candidateLogs[0] || null;

      const isDigitallySigned = Boolean(
        latestLog?.signature_validation_code || latestLog?.signed_at,
      );

      let signedProfessionalSignature: string | null = null;
      const professionalId =
        latestLog?.professional_id ||
        foundItem?.professional_id ||
        treatment?.professional_id;

      if (professionalId && isDigitallySigned) {
        const profResponse = await api.get(`/professionals/${professionalId}`);
        signedProfessionalSignature = profResponse.data?.signature || null;
        setSignerName(
          profResponse.data?.name || treatment?.professional_name || null,
        );
      } else {
        setSignerName(treatment?.professional_name ?? null);
      }

      setTreatment(treatment);
      setItem(foundItem ?? null);
      setLatestLogId(latestLog?.id || null);
      setLatestLogProfessionalId(latestLog?.professional_id || null);
      setValidationCode(latestLog?.signature_validation_code ?? null);
      setSignedAt(latestLog?.signed_at ?? null);
      setSignature(signedProfessionalSignature);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description:
          error?.response?.data?.message || 'Erro ao carregar os dados.',
      });
    } finally {
      setLoading(false);
    }
  }, [id, itemId, addToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <Loading load />;

  const isDigitallySigned = Boolean(validationCode || signedAt);

  return (
    <>
      <ATitle icon={<PrinterOutlined />}>
        Imprimir Procedimento Odontológico
      </ATitle>

      <ContainerMenuPrint>
        <AButton
          type="default"
          onClick={() => navigate(`/dental/${treatment?.id}/evolution`)}
          icon={<LeftCircleOutlined />}
        >
          Voltar
        </AButton>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isDigitallySigned ? (
            <Tag color="success" style={{ fontSize: 13, padding: '4px 10px' }}>
              ✓ Procedimento assinado digitalmente
            </Tag>
          ) : (
            <AButton
              type="dashed"
              onClick={() => setModalSign(true)}
              disabled={!latestLogId}
              icon={<SignatureOutlined />}
              style={{ backgroundColor: '#33cf5c', color: '#fff' }}
            >
              Assinar procedimento
            </AButton>
          )}
        </div>
      </ContainerMenuPrint>

      {!latestLogId && (
        <p style={{ marginTop: 4, color: '#a8071a', fontWeight: 600 }}>
          Este procedimento ainda não possui evolução registrada para
          assinatura.
        </p>
      )}

      {validationCode && (
        <p style={{ marginTop: 4, fontWeight: 600 }}>
          Código de validação: {validationCode}
        </p>
      )}

      <DentalTreatmentProcedureReport
        treatment={treatment}
        item={item}
        signature={signature}
        validationCode={validationCode}
        signedAt={signedAt}
        signerName={signerName}
      />

      <ToSignature
        open={modalSign}
        close={() => setModalSign(false)}
        professional_id={
          latestLogProfessionalId ||
          item?.professional_id ||
          treatment?.professional_id ||
          null
        }
        onAuthenticated={async (authData, password) => {
          if (!latestLogId) {
            addToast({
              type: 'warning',
              title: 'Sem evolução',
              description:
                'Não foi encontrado log de evolução para este procedimento.',
            });
            return;
          }

          const signResponse = await api.post('/signature-validation/sign', {
            document_type: 'DENTAL_TREATMENT_EVOLUTION',
            record_id: latestLogId,
            professional_id:
              latestLogProfessionalId ||
              item?.professional_id ||
              treatment?.professional_id,
            password,
          });

          setSignature(
            authData.signature ||
              signResponse.data.professional_signature ||
              null,
          );
          setValidationCode(signResponse.data.validation_code ?? null);
          setSignedAt(signResponse.data.signed_at ?? new Date().toISOString());
          setSignerName(
            signResponse.data?.professional?.name ||
              treatment?.professional_name ||
              signerName,
          );

          addToast({
            type: 'success',
            title: 'Procedimento assinado',
            description: 'Procedimento assinado digitalmente com sucesso.',
          });
        }}
      />
    </>
  );
};

export default DentalTreatmentProcedurePrint;
